import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Facebook } from '@freelancer/facebook';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { isDefined } from '@freelancer/utils';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { transformAuthResponse, transformResponseData, transformTwoFactorResponse, transformUserCreateResonse, } from './login-signup.transformers';
import * as i0 from "@angular/core";
import * as i1 from "../auth/auth.service";
import * as i2 from "../auth/auth.config";
import * as i3 from "@angular/common/http";
import * as i4 from "../freelancer-http/freelancer-http.service";
import * as i5 from "@angular/router";
import * as i6 from "../facebook/facebook.service";
import * as i7 from "../location/location.service";
import * as i8 from "@laurentgoudet/ngx-cookie";
/**
 * Service which provides an interface to backend endpoints for the login/signup funnel
 */
var LoginSignupService = /** @class */ (function () {
    function LoginSignupService(auth, authConfig, rawHttp, freelancerHttp, router, activatedRoute, facebook, location, cookies) {
        this.auth = auth;
        this.authConfig = authConfig;
        this.rawHttp = rawHttp;
        this.freelancerHttp = freelancerHttp;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.facebook = facebook;
        this.location = location;
        this.cookies = cookies;
        this.savedState = {
            user: '',
            password: '',
        };
    }
    LoginSignupService.prototype.login = function (user, password) {
        var _this = this;
        return this.auth.deviceToken$
            .pipe(take(1))
            .toPromise()
            .then(function (deviceToken) {
            // return a custom error because passing undefined
            // to the backend will result in `InvalidInput`
            if (!deviceToken) {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
                };
            }
            return _this.freelancerHttp
                .post('auth/login.php', {
                user: user,
                password: password,
                device_token: deviceToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1), map(function (responseData) {
                return transformResponseData(responseData, transformAuthResponse);
            }))
                .toPromise();
        });
    };
    LoginSignupService.prototype.verify = function (preLoginToken, password) {
        var _this = this;
        return this.auth.deviceToken$
            .pipe(take(1))
            .toPromise()
            .then(function (deviceToken) {
            if (!deviceToken) {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
                };
            }
            return _this.freelancerHttp
                .post('auth/verify2FALogin.php', {
                totp: password,
                device_token: deviceToken,
                pre_login_token: preLoginToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1))
                .toPromise();
        });
    };
    LoginSignupService.prototype.resend2FACode = function (preLoginToken, method) {
        var _this = this;
        return this.auth.deviceToken$
            .pipe(take(1))
            .toPromise()
            .then(function (deviceToken) {
            if (!deviceToken) {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
                };
            }
            return _this.freelancerHttp
                .post('auth/send2FAToken.php', {
                '2fa_method': method,
                device_token: deviceToken,
                pre_login_token: preLoginToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1), map(function (responseData) {
                return transformResponseData(responseData, transformTwoFactorResponse);
            }))
                .toPromise();
        });
    };
    LoginSignupService.prototype.facebookLogin = function (authResponse) {
        return this.freelancerHttp
            .post('auth/facebookLogin.php', {
            app_id: this.facebook.appId,
            credentials: authResponse.signedRequest,
            access_token: authResponse.accessToken,
        }, {
            isGaf: true,
            serializeBody: true,
            errorWhitelist: [
                ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS,
                ErrorCodeApi.AUTH_USER_MISSING,
            ],
        })
            .pipe(take(1))
            .toPromise();
    };
    LoginSignupService.prototype.facebookLinkLogin = function (email, password, authResponse) {
        return this.freelancerHttp
            .post('auth/facebookLinkLogin.php', {
            email: email,
            password: password,
            credentials: authResponse.signedRequest,
            access_token: authResponse.accessToken,
        }, {
            isGaf: true,
            serializeBody: true,
        })
            .pipe(take(1))
            .toPromise();
    };
    LoginSignupService.prototype.checkUserDetails = function (user, password) {
        return this.freelancerHttp
            .post('users/0.1/users/check', {
            user: user,
            password: password,
        })
            .pipe(take(1))
            .toPromise();
    };
    LoginSignupService.prototype.signup = function (_a) {
        var email = _a.email, username = _a.username, role = _a.role, password = _a.password, privacyConsent = _a.privacyConsent, personalUse = _a.personalUse, facebookAuth = _a.facebookAuth, captcha = _a.captcha;
        var _b = this.activatedRoute.snapshot.queryParams, project_collaboration_invitation = _b.project_collaboration_invitation, contest_collaboration_invitation = _b.contest_collaboration_invitation;
        var referralSignup = this.authConfig.referralCookie
            ? this.cookies.get(this.authConfig.referralCookie)
            : undefined;
        // if facebook data is provided, use facebook endpoint
        var endpoint = facebookAuth
            ? 'users/0.1/users/facebook'
            : 'users/0.1/users';
        return this.freelancerHttp
            .post(endpoint, {
            user: {
                email: email,
                username: username,
                role: role,
            },
            password: password,
            captcha: captcha,
            signup_meta: {
                privacy_consent: privacyConsent,
            },
            facebook_id: facebookAuth ? facebookAuth.userID : undefined,
            access_token: facebookAuth ? facebookAuth.accessToken : undefined,
            extra: {
                referral_signup: referralSignup,
                project_collaboration_invitation: project_collaboration_invitation,
                contest_collaboration_invitation: contest_collaboration_invitation,
                business_user: 
                // use value if set; defaults to false
                isDefined(personalUse) && !personalUse
                    ? // this endpoint only accepts strings
                        'true'
                    : 'false',
                // send this here as well because facebook signup doesn't use signup_meta
                privacy_consent: 
                // use value if set; defauls to true
                isDefined(privacyConsent) && !privacyConsent ? 'false' : 'true',
            },
        }, {})
            .pipe(take(1), map(function (response) {
            return transformResponseData(response, transformUserCreateResonse);
        }))
            .toPromise();
    };
    /**
     * Handle successful authentication, be it on login or signup
     */
    LoginSignupService.prototype.handleSuccess = function (action, userId, authToken, rememberLogin, redirectParams) {
        var _this = this;
        // set auth session (automatically sets cookies)
        this.auth.setSession(userId.toString(), authToken);
        // clear referral signup cookies if set
        if (this.authConfig.referralCookie) {
            this.cookies.remove(this.authConfig.referralCookie);
        }
        var crossDomainSSOPromise = this.setCrossDomainSSO(userId, authToken, rememberLogin);
        var redirectPromise = this.getRedirectUrl(action, redirectParams);
        // wait for both reqest chains to finish, then redirect
        return Promise.all([redirectPromise, crossDomainSSOPromise]).then(function (_a) {
            var redirectUrl = _a[0];
            if (!redirectUrl) {
                return;
            }
            if (redirectUrl.startsWith('http')) {
                // absolute URL: do a hard navigate
                _this.location.redirect(redirectUrl);
                return;
            }
            // parse query params and fragments in the nextUrl
            var urlTree = _this.router.parseUrl(redirectUrl);
            _this.location.navigateByUrl(urlTree);
        });
    };
    /**
     * Logs a user in on other domains that they should be logged in on.
     * Fetches domains from `getCrossDomainSSODomains.php`, then sends requests
     * to those domains to set cookies on them.
     */
    LoginSignupService.prototype.setCrossDomainSSO = function (userId, authToken, rememberLogin) {
        var _this = this;
        // this is equivalent to the `serializeBody` option for freelancerHttp
        // needs to be manually done since we're using raw httpClient for the other domains
        var formBody = this.freelancerHttp._serialize({
            userId: userId,
            authToken: authToken,
            rememberLogin: rememberLogin,
        });
        var headers = new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded');
        return this.freelancerHttp
            .get('auth/getCrossDomainSSODomains.php', { isGaf: true })
            .pipe(take(1))
            .toPromise()
            .then(function (response) {
            if (response.status === 'error') {
                return;
            }
            return Promise.all(response.result.domains.map(function (domain) {
                return _this.rawHttp
                    .post(domain + "/ajax-api/auth/crossDomainSSO.php", formBody, {
                    headers: headers,
                    withCredentials: true,
                })
                    .pipe(
                // silence any errors
                catchError(function (_) { return Promise.resolve(); }), take(1))
                    .toPromise();
            })).then();
        });
    };
    /**
     * Returns the next route to load after logging in or signing up
     *
     * Note that this is only called on successful logins and other redirections
     * may still occur for failed attempts.
     *
     * Eg. Suspended users may be redirected to the suspended account page.
     */
    LoginSignupService.prototype.getRedirectUrl = function (action, _a) {
        var _b = (_a === void 0 ? {} : _a).redirectDisabled, redirectDisabled = _b === void 0 ? false : _b;
        var _c = this.activatedRoute.snapshot.queryParams, next = _c.next, goto = _c.goto, project_collaboration_invitation = _c.project_collaboration_invitation, contest_collaboration_invitation = _c.contest_collaboration_invitation;
        var endpoint = action === 'login'
            ? 'auth/getLoginRedirectUrl.php'
            : 'auth/getSignupRedirectUrl.php';
        // TODO: go to new-freelancer for freelancers on signup.
        var defaultUrl = 'dashboard';
        return this.freelancerHttp
            .post(endpoint, {
            redirect_disabled: redirectDisabled.toString(),
            next: next && decodeURIComponent(next),
            goto: goto,
            project_collaboration_invitation: project_collaboration_invitation,
            contest_collaboration_invitation: contest_collaboration_invitation,
        }, { isGaf: true, serializeBody: true })
            .pipe(take(1))
            .toPromise()
            .then(function (response) {
            return response.status === 'success'
                ? response.result.redirect_url
                : defaultUrl;
        });
    };
    LoginSignupService.prototype.resetPassword = function (email) {
        return this.rawHttp
            .post(this.authConfig.baseUrl + "/forgot", this.freelancerHttp._serialize({ email: email }))
            .pipe(map(function () {
            return ({
                status: 'success',
                result: undefined,
            });
        }), catchError(function (_) {
            return Rx.of({
                status: 'error',
                errorCode: 'UNKNOWN_ERROR',
            });
        }))
            .toPromise();
    };
    /**
     * Saves state that needs to be shared across login / signup when switching routes
     */
    LoginSignupService.prototype.saveState = function (state) {
        this.savedState = state;
    };
    /**
     * Returns and clears the saved state.
     */
    LoginSignupService.prototype.getSavedState = function () {
        var state = this.savedState;
        this.savedState = {
            user: '',
            password: '',
        };
        return state;
    };
    LoginSignupService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function LoginSignupService_Factory() { return new LoginSignupService(i0.ɵɵinject(i1.Auth), i0.ɵɵinject(i2.AUTH_CONFIG), i0.ɵɵinject(i3.HttpClient), i0.ɵɵinject(i4.FreelancerHttp), i0.ɵɵinject(i5.Router), i0.ɵɵinject(i5.ActivatedRoute), i0.ɵɵinject(i6.Facebook), i0.ɵɵinject(i7.Location), i0.ɵɵinject(i8.CookieService)); }, token: LoginSignupService, providedIn: "root" });
    return LoginSignupService;
}());
export { LoginSignupService };
